import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccommodationDetailPolicy } from '../../../api/asset/assetApiTypes';
import InputField from '../../../components/ui/InputField';
import TextareaField from '../../../components/ui/TextareaField';
import AssetDetailSection from './AssetDetailSection';

interface PoliciesSectionProps {
    policies: AccommodationDetailPolicy[] | undefined;
    mode: 'view' | 'edit' | 'create';
    onPoliciesChange?: (policies: AccommodationDetailPolicy[]) => void;
}

const PoliciesSection: React.FC<PoliciesSectionProps> = ({ policies, mode, onPoliciesChange }) => {
    const { t, i18n } = useTranslation();
    const isEditing = mode === 'edit' || mode === 'create';
    const hasPolicies = policies && policies.length > 0;
    const lang = i18n.language.startsWith('de') ? 'de' : 'en';

    const handleTitleChange = (index: number, langKey: string, value: string) => {
        if (!policies) return;
        const policy = policies[index];
        const titles = { ...policy.titles, [langKey]: value };
        const updated = policies.map((p, i) => i === index ? { ...p, titles, title: titles.de || titles.en || '' } : p);
        onPoliciesChange?.(updated);
    };

    const handleDescriptionChange = (index: number, langKey: string, value: string) => {
        if (!policies) return;
        const policy = policies[index];
        const descriptions = { ...policy.descriptions, [langKey]: value };
        const updated = policies.map((p, i) =>
            i === index ? { ...p, descriptions, description: descriptions.de || descriptions.en || '' } : p,
        );
        onPoliciesChange?.(updated);
    };

    const handleAddPolicy = () => {
        const newPolicy: AccommodationDetailPolicy = {
            title: '',
            titles: { de: '', en: '' },
            description: '',
            descriptions: { de: '', en: '' },
            icon: '',
        };
        onPoliciesChange?.([...(policies ?? []), newPolicy]);
    };

    const handleRemovePolicy = (index: number) => {
        onPoliciesChange?.((policies ?? []).filter((_, i) => i !== index));
    };

    const getLocalizedTitle = (policy: AccommodationDetailPolicy) =>
        policy.titles?.[lang] || policy.title;

    const getLocalizedDescription = (policy: AccommodationDetailPolicy) =>
        policy.descriptions?.[lang] || policy.description;

    return (
        <AssetDetailSection title={t('asset_detail.sections.policies')}>
            {isEditing ? (
                <div className={'flex flex-col gap-4'}>
                    {hasPolicies && policies.map((policy, i) => (
                        <div key={i} className={'border border-foreground/10 rounded-lg p-4 flex flex-col gap-4 relative'}>
                            <button
                                type={'button'}
                                onClick={() => handleRemovePolicy(i)}
                                className={'absolute top-2 right-2 text-red-400 hover:text-red-600 text-xl px-2'}
                                title={t('asset_detail.actions.remove')}
                            >
                                &times;
                            </button>
                            <div className={'flex flex-col gap-2'}>
                                <InputField
                                    label={`${t('asset_detail.fields.policy_title')} (DE)`}
                                    value={policy.titles?.de ?? ''}
                                    onChange={(val) => handleTitleChange(i, 'de', val)}
                                />
                                <InputField
                                    label={`${t('asset_detail.fields.policy_title')} (EN)`}
                                    value={policy.titles?.en ?? ''}
                                    onChange={(val) => handleTitleChange(i, 'en', val)}
                                />
                            </div>
                            <div className={'flex flex-col gap-2'}>
                                <TextareaField
                                    label={`${t('asset_detail.fields.policy_description')} (DE)`}
                                    value={policy.descriptions?.de ?? ''}
                                    onChange={(val) => handleDescriptionChange(i, 'de', val)}
                                    rows={3}
                                />
                                <TextareaField
                                    label={`${t('asset_detail.fields.policy_description')} (EN)`}
                                    value={policy.descriptions?.en ?? ''}
                                    onChange={(val) => handleDescriptionChange(i, 'en', val)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    ))}
                    <button
                        type={'button'}
                        onClick={handleAddPolicy}
                        className={'text-sm text-primary hover:text-primary/80 font-medium text-left'}
                    >
                        {t('asset_detail.actions.add_policy')}
                    </button>
                </div>
            ) : !hasPolicies ? (
                <p className={'text-sm text-foreground/40 italic'}>
                    {t('asset_detail.empty_states.policies')}
                </p>
            ) : (
                <div className={'flex flex-col gap-3'}>
                    {policies.map((policy, i) => (
                        <details key={i} className={'group'}>
                            <summary
                                className={'flex items-center gap-2 cursor-pointer text-sm font-medium text-foreground hover:text-primary transition-colors'}
                            >
                                <svg
                                    className={'w-4 h-4 text-foreground/40 transition-transform group-open:rotate-90'}
                                    fill={'none'}
                                    viewBox={'0 0 24 24'}
                                    stroke={'currentColor'}
                                    strokeWidth={2}
                                >
                                    <path strokeLinecap={'round'} strokeLinejoin={'round'} d={'M9 5l7 7-7 7'} />
                                </svg>
                                {getLocalizedTitle(policy)}
                            </summary>
                            <div className={'ml-6 mt-2 text-sm text-foreground/80'}>
                                <p>{getLocalizedDescription(policy)}</p>
                            </div>
                        </details>
                    ))}
                </div>
            )}
        </AssetDetailSection>
    );
};

export default PoliciesSection;
